import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClipboardListIcon,
  DownloadIcon,
  ExclamationIcon,
} from '@heroicons/react/outline';

import { GlobalContext } from '@/context';
import { appointmentService, patientIntakeService } from '@/services';
import {
  INTAKE_GRACE_HOURS,
  pickIntakeAppointment,
} from '@/constants/intakeForm';

// Renders for the patient sidebar's "Intake" entry. We don't know which
// appointment to deep-link to until we look it up, so this view does the
// lookup and redirects (or explains why we can't).
const IntakeRedirect = () => {
  const history = useHistory();
  const { currentUser } = useContext(GlobalContext);
  const [state, setState] = useState({
    phase: 'loading', // loading | redirecting | none | submitted | error
    appointmentId: null,
    pdfS3Key: null,
  });

  useEffect(() => {
    if (!currentUser || !currentUser.uid) return;
    let cancelled = false;
    (async () => {
      try {
        // Include appointments that ended within the grace window (not just
        // future ones) so the intake link stays reachable for a few days after
        // the visit instead of vanishing the moment the appointment ends.
        const graceStart = new Date(
          Date.now() - INTAKE_GRACE_HOURS * 60 * 60 * 1000,
        ).toISOString();
        const {
          data: { data: appointments },
        } = await appointmentService.listAppointments({
          patient_id: currentUser.uid,
          appointment_end_after: graceStart,
          sort: 'appointment_start_time',
          order: 'ASC',
        });
        if (cancelled) return;
        const next = pickIntakeAppointment(appointments);
        if (!next || !next.uid) {
          setState({ phase: 'none', appointmentId: null, pdfS3Key: null });
          return;
        }
        const { data: intake } = await patientIntakeService.getIntake(next.uid);
        if (cancelled) return;
        const status = (intake && intake.status) || 'pending';
        if (status === 'completed') {
          setState({
            phase: 'submitted',
            appointmentId: next.uid,
            pdfS3Key: (intake && intake.pdf_s3_key) || null,
          });
          return;
        }
        setState({
          phase: 'redirecting',
          appointmentId: next.uid,
          pdfS3Key: null,
        });
        history.replace(`/intake/${next.uid}`);
      } catch (err) {
        if (cancelled) return;
        // 403/404 from a stale intake row or a leaked URL — treat as "no intake
        // available right now" instead of a hard error so the patient isn't
        // staring at a red wall they can't act on.
        setState({ phase: 'none', appointmentId: null, pdfS3Key: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, history]);

  const handleDownload = async () => {
    if (!state.pdfS3Key) return;
    try {
      const url = await patientIntakeService.getPdfViewUrl(state.pdfS3Key);
      if (url) window.open(url, '_blank');
    } catch (_) {
      // No-op: provider portal exposes the PDF too, so this failing is not
      // catastrophic.
    }
  };

  if (state.phase === 'loading' || state.phase === 'redirecting') {
    return (
      <div className="bg-mmj-lightblue border border-mmj-blue rounded-xl p-6">
        <div className="flex items-center space-x-3">
          <ClipboardListIcon className="h-8 w-8 text-mmj-blue flex-shrink-0" />
          <div className="text-base text-mmj-header">
            Looking up your intake form&hellip;
          </div>
        </div>
      </div>
    );
  }

  if (state.phase === 'submitted') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-3">
          <CheckCircleIcon className="h-8 w-8 text-green-500 flex-shrink-0" />
          <div className="font-bold text-lg text-green-800">
            Intake Submitted
          </div>
        </div>
        <p className="text-sm text-green-700 mb-4">
          Your intake form is complete. Your doctor will review it before your
          appointment.
        </p>
        {state.pdfS3Key ? (
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center space-x-2 bg-mmj-blue text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity"
            style={{ minHeight: '48px', fontSize: '16px' }}
          >
            <DownloadIcon className="h-5 w-5" aria-hidden="true" />
            <span>Download a copy</span>
          </button>
        ) : (
          <div className="text-sm text-green-700">
            Your PDF copy is being prepared. Check back in a moment.
          </div>
        )}
      </div>
    );
  }

  // phase === 'none' or 'error'
  return (
    <div className="bg-mmj-lightblue border border-mmj-blue rounded-xl p-6">
      <div className="flex items-center space-x-3 mb-3">
        <ExclamationIcon className="h-8 w-8 text-mmj-blue flex-shrink-0" />
        <div className="font-bold text-lg text-mmj-header">
          No intake form available
        </div>
      </div>
      <p className="text-sm text-mmj-header opacity-70 mb-4">
        Your intake form unlocks after you schedule your appointment. Once you
        book, the form will appear here and in your confirmation email.
      </p>
      <button
        type="button"
        onClick={() => history.push('/schedule')}
        className="bg-mmj-blue text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity"
        style={{ minHeight: '48px', fontSize: '16px' }}
      >
        Schedule an appointment
      </button>
    </div>
  );
};

export default IntakeRedirect;
